<template>
  <v-card outlined class="rounded-lg">
    <v-toolbar color="deep-purple darken-2" dark dense flat>
      <v-icon class="mr-2">mdi-rocket-launch-outline</v-icon>
      <v-toolbar-title class="subtitle-1 font-weight-bold">
        <div>Anticipar inicio de módulo</div>
        <div v-if="programaNombre" class="caption deep-purple--text text--lighten-3" style="line-height:1.2; font-weight:400">
          {{ programaNombre }}
        </div>
      </v-toolbar-title>
      <v-spacer />
      <v-btn icon :loading="loading" @click="cargarHistorial" title="Actualizar">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-toolbar>

    <v-card-text class="pa-4">

      <!-- Botones para solicitar por módulo -->
      <div class="d-flex flex-wrap mb-4" style="gap:10px">
        <div
          v-for="mod in [0, 1]"
          :key="mod"
          class="d-flex align-center pa-3 flex-grow-1 rounded"
          :style="'background:' + (mod === 0 ? '#ede7f6' : '#e8eaf6') + '; border-left:4px solid ' + (mod === 0 ? '#6a1b9a' : '#3949ab')"
        >
          <div class="flex-grow-1">
            <div class="overline" style="font-size:10px;color:#546e7a">Módulo {{ mod }}</div>
            <div class="caption font-weight-medium">
              <template v-if="pendientePorModulo(mod)">
                <v-chip x-small color="orange darken-2" dark>SOLICITADO</v-chip>
                <span class="ml-1 grey--text">Hay una solicitud pendiente</span>
              </template>
              <span v-else class="grey--text text--darken-1">Sin solicitud pendiente</span>
            </div>
          </div>
          <v-btn
            small
            :color="mod === 0 ? 'deep-purple darken-2' : 'indigo darken-2'"
            dark
            :disabled="pendientePorModulo(mod)"
            @click="abrirSolicitud(mod)"
          >
            <v-icon left small>mdi-plus</v-icon>Solicitar
          </v-btn>
        </div>
      </div>

      <!-- Cargando -->
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="deep-purple darken-2" />
        <div class="caption grey--text mt-2">Cargando historial...</div>
      </div>

      <!-- Sin historial -->
      <div v-else-if="historial.length === 0" class="text-center py-8 grey--text">
        <v-icon large color="grey lighten-2">mdi-rocket-outline</v-icon>
        <div class="mt-2 caption">Sin solicitudes de anticipo registradas.</div>
      </div>

      <!-- Timeline -->
      <v-timeline v-else dense clipped>
        <v-timeline-item
          v-for="sol in historial"
          :key="sol.id"
          :color="colorEstado(sol.estado)"
          small
        >
          <v-card outlined class="mb-2 rounded">
            <v-card-text class="py-2 px-3">
              <!-- Fila superior: módulo + estado + fecha + acciones -->
              <div class="d-flex align-center flex-wrap mb-1" style="gap:6px">
                <v-chip x-small color="blue-grey darken-1" dark label>
                  Módulo {{ sol.modulo_objetivo }}
                </v-chip>
                <v-chip x-small :color="colorEstado(sol.estado)" dark>{{ sol.estado }}</v-chip>
                <span class="caption grey--text">{{ formatFecha(sol.fecha_registro) }}</span>
                <v-spacer />
                <template v-if="sol.estado === 'SOLICITADO' && puedeAprobar">
                  <v-btn
                    x-small color="green darken-1" dark
                    :loading="procesando === sol.id + '_ap'"
                    :disabled="procesando !== null"
                    @click="aprobar(sol)"
                  >
                    <v-icon left x-small>mdi-check</v-icon>Aprobar
                  </v-btn>
                  <v-btn
                    x-small color="red darken-1" dark
                    :loading="procesando === sol.id + '_re'"
                    :disabled="procesando !== null"
                    @click="abrirRechazar(sol)"
                  >
                    <v-icon left x-small>mdi-close</v-icon>Rechazar
                  </v-btn>
                </template>
              </div>

              <!-- Descripción -->
              <div class="caption grey--text text--darken-1" v-if="sol.descripcion">
                <v-icon x-small class="mr-1">mdi-text-box-outline</v-icon>{{ sol.descripcion }}
              </div>

              <!-- Respuesta/motivo -->
              <div class="caption mt-1" v-if="sol.respuesta">
                <v-icon x-small color="red darken-1" class="mr-1">mdi-message-reply-text</v-icon>
                <span class="red--text text--darken-1">Respuesta: {{ sol.respuesta }}</span>
              </div>

              <!-- Solicitante -->
              <div class="caption grey--text mt-1" v-if="sol.solicitante_nombres">
                <v-icon x-small class="mr-1">mdi-account-outline</v-icon>
                {{ sol.solicitante_nombres }} {{ sol.solicitante_primer_apellido }}
              </div>

              <!-- Fecha de respuesta -->
              <div class="caption grey--text mt-1" v-if="sol.fecha_respuesta_solicitud">
                <v-icon x-small class="mr-1">mdi-calendar-check</v-icon>
                Respondida: {{ formatFecha(sol.fecha_respuesta_solicitud) }}
              </div>
            </v-card-text>
          </v-card>
        </v-timeline-item>
      </v-timeline>
    </v-card-text>

    <!-- ── Dialog nueva solicitud ── -->
    <v-dialog v-model="dialogSolicitud" max-width="480" persistent>
      <v-card>
        <v-card-title class="py-4 px-5">
          <v-icon color="deep-purple darken-2" class="mr-2">mdi-rocket-launch-outline</v-icon>
          <span class="subtitle-1 font-weight-bold">Solicitar anticipo — Módulo {{ moduloSeleccionado }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 px-5 pb-1">
          <v-alert type="info" dense outlined class="mb-3" icon="mdi-information-outline">
            Solicita autorización para adelantar el inicio del <strong>Módulo {{ moduloSeleccionado }}</strong>.
            Un responsable académico revisará la solicitud.
          </v-alert>
          <v-textarea
            v-model="form.descripcion"
            label="Justificación *"
            outlined dense rows="3"
            placeholder="Explica el motivo por el que se solicita anticipar el módulo..."
            counter="500"
            autofocus
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn text small @click="cerrarDialogSolicitud" :disabled="creando">Cancelar</v-btn>
          <v-btn
            color="deep-purple darken-2" dark small
            :loading="creando"
            :disabled="!form.descripcion.trim()"
            @click="solicitar"
          >
            <v-icon left small>mdi-send</v-icon>Enviar solicitud
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Dialog rechazar ── -->
    <v-dialog v-model="dialogRechazar" max-width="420" persistent>
      <v-card>
        <v-card-title class="py-4 px-5">
          <v-icon color="red darken-1" class="mr-2">mdi-close-circle-outline</v-icon>
          <span class="subtitle-1 font-weight-bold">Rechazar solicitud</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 px-5 pb-1">
          <v-textarea
            v-model="formRechazar.respuesta"
            label="Motivo del rechazo *"
            outlined dense rows="3"
            placeholder="Indica el motivo por el que se rechaza la solicitud..."
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn text small @click="dialogRechazar = false" :disabled="procesando !== null">Cancelar</v-btn>
          <v-btn
            color="red darken-1" dark small
            :loading="procesando !== null"
            :disabled="!formRechazar.respuesta.trim()"
            @click="rechazar"
          >
            <v-icon left small>mdi-close</v-icon>Rechazar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right :timeout="4000">
      {{ snackbar.msg }}
      <template v-slot:action>
        <v-btn text small @click="snackbar.show = false">OK</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import xaxios from '@/xaxios';

const API = process.env.VUE_APP_API_REST;

export default {
  name: 'SolicitudAnticipoModulo',

  props: {
    programaId: { type: [Number, String], required: true },
    programaNombre: { type: String, default: null },
  },

  data() {
    return {
      historial: [],
      loading: false,
      creando: false,
      procesando: null,
      dialogSolicitud: false,
      dialogRechazar: false,
      moduloSeleccionado: null,
      solicitudAgestionar: null,
      form: { descripcion: '' },
      formRechazar: { respuesta: '' },
      snackbar: { show: false, msg: '', color: 'success' },
    };
  },

  computed: {
    puedeAprobar() {
      const cargo = this.$store.state.CARGO?.id;
      return cargo === 1 || cargo === 3;
    },
  },

  methods: {
    pendientePorModulo(modulo) {
      return this.historial.some(
        (s) => s.modulo_objetivo === modulo && s.estado === 'SOLICITADO'
      );
    },

    async cargarHistorial() {
      this.loading = true;
      try {
        const { data } = await xaxios.get(
          `${API}/api/v1/programa/${this.programaId}/anticipo-modulo/solicitudes`
        );
        this.historial = Array.isArray(data.data?.historial) ? data.data.historial : [];
      } catch {
        this.historial = [];
      } finally {
        this.loading = false;
      }
    },

    abrirSolicitud(modulo) {
      this.moduloSeleccionado = modulo;
      this.form.descripcion = '';
      this.dialogSolicitud = true;
    },

    cerrarDialogSolicitud() {
      this.dialogSolicitud = false;
      this.form.descripcion = '';
      this.moduloSeleccionado = null;
    },

    async solicitar() {
      if (!this.form.descripcion.trim()) return;
      this.creando = true;
      try {
        await xaxios.post(
          `${API}/api/v1/programa/${this.programaId}/anticipo-modulo/solicitudes`,
          { modulo_objetivo: this.moduloSeleccionado, descripcion: this.form.descripcion }
        );
        this.notificar('Solicitud enviada correctamente', 'success');
        this.cerrarDialogSolicitud();
        await this.cargarHistorial();
      } catch (e) {
        const msg = e?.response?.data?.error || e?.response?.data?.message || 'Error al enviar la solicitud';
        this.notificar(msg, 'error');
      } finally {
        this.creando = false;
      }
    },

    async aprobar(sol) {
      this.procesando = sol.id + '_ap';
      try {
        await xaxios.patch(
          `${API}/api/v1/anticipo-modulo/solicitudes/${sol.id}`,
          { estado: 'APROBADO' }
        );
        this.notificar('Solicitud aprobada correctamente', 'success');
        await this.cargarHistorial();
      } catch (e) {
        const msg = e?.response?.status === 403
          ? 'No tienes permiso para aprobar solicitudes'
          : e?.response?.data?.error || e?.response?.data?.message || 'Error al aprobar';
        this.notificar(msg, 'error');
      } finally {
        this.procesando = null;
      }
    },

    abrirRechazar(sol) {
      this.solicitudAgestionar = sol;
      this.formRechazar.respuesta = '';
      this.dialogRechazar = true;
    },

    async rechazar() {
      if (!this.formRechazar.respuesta.trim()) return;
      this.procesando = this.solicitudAgestionar.id + '_re';
      try {
        await xaxios.patch(
          `${API}/api/v1/anticipo-modulo/solicitudes/${this.solicitudAgestionar.id}`,
          { estado: 'RECHAZADO', respuesta: this.formRechazar.respuesta }
        );
        this.notificar('Solicitud rechazada', 'warning');
        this.dialogRechazar = false;
        this.solicitudAgestionar = null;
        await this.cargarHistorial();
      } catch (e) {
        const msg = e?.response?.status === 403
          ? 'No tienes permiso para rechazar solicitudes'
          : e?.response?.data?.error || e?.response?.data?.message || 'Error al rechazar';
        this.notificar(msg, 'error');
      } finally {
        this.procesando = null;
      }
    },

    colorEstado(estado) {
      const map = { SOLICITADO: 'orange darken-2', APROBADO: 'green darken-2', RECHAZADO: 'red darken-1' };
      return map[estado] || 'grey';
    },

    formatFecha(iso) {
      if (!iso) return '—';
      try {
        return new Date(iso).toLocaleDateString('es-BO', {
          day: '2-digit', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });
      } catch {
        return iso;
      }
    },

    notificar(msg, color = 'success') {
      this.snackbar = { show: true, msg, color };
    },
  },

  mounted() {
    this.cargarHistorial();
  },
};
</script>
